import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Mail, Phone, Printer } from "lucide-react";
import { api } from "./api";
import { Badge } from "./ui";

/* Liste → « Essais » : qui vient à l'essai cette semaine, tous les cours confondus (Jen,
   03.11.2026 : « est-ce qu'il y a un moyen de facilement voir qui sont les essais cette
   semaine ? … par défaut la semaine en cours, mais on peut cliquer avec la flèche une
   semaine plus loin, ou sélectionner les dates »). Groupés par jour, triés par heure ; une
   carte par essai sur téléphone, une ligne par essai sur grand écran et sur le papier.
   Les annulés ne sont pas montrés, seulement comptés. */

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre",
  "octobre", "novembre", "décembre"];
// Au-delà, le serveur refuse : ce n'est plus « les essais de la semaine ».
const JOURS_MAX = 93;
// Le turquoise de l'école, assez foncé pour se lire en petit sur fond blanc.
const LIEN = "#0E7490";
// Une colonne d'heure fixe : chaque ligne est sa propre grille, les colonnes restent alignées.
const COLONNES = "96px minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 1.5fr)";

const versDate = (iso) => { const [a, m, j] = String(iso).split("-").map(Number); return new Date(a, m - 1, j); };
const versIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const decaler = (iso, jours) => { const d = versDate(iso); d.setDate(d.getDate() + jours); return versIso(d); };
const ecart = (du, au) => Math.round((versDate(au) - versDate(du)) / 86400000);
const jourMois = (d, annee) => `${d.getDate() === 1 ? "1er" : d.getDate()} ${MOIS[d.getMonth()]}${annee ? ` ${d.getFullYear()}` : ""}`;

/* « Semaine du 2 au 8 novembre », « Semaine du 26 octobre au 1er novembre », « Du 3 au 20 novembre ». */
const libellePeriode = (du, au) => {
  const d = versDate(du);
  const a = versDate(au);
  const autreAnnee = d.getFullYear() !== a.getFullYear();
  const debut = d.getMonth() === a.getMonth() && !autreAnnee
    ? (d.getDate() === 1 ? "1er" : String(d.getDate()))
    : jourMois(d, autreAnnee);
  const texte = `du ${debut} au ${jourMois(a, autreAnnee)}`;
  const semaine = d.getDay() === 1 && ecart(du, au) === 6;
  return semaine ? `Semaine ${texte}` : texte[0].toUpperCase() + texte.slice(1);
};

const telephone = (t) => `tel:${String(t).replace(/[^\d+]/g, "")}`;
const ouvrirFiche = (id) => window.dispatchEvent(new CustomEvent("console-aller", { detail: { onglet: "eleves", id } }));

/* Absent·e à l'appel (rouge) ou venu·e (vert) ; un statut inhabituel s'affiche tel quel.
   « Inscrit » (ou rien) : l'essai est simplement réservé, pas de pastille. */
const Etat = ({ e }) => {
  if (e.presence === "absent") return <Badge ton="rouge" testid="essai-absent">absent·e</Badge>;
  if (e.presence === "present") return <Badge ton="vert" testid="essai-venu">venu·e</Badge>;
  if (e.statut && !/^inscrit/i.test(e.statut)) return <Badge ton="neutre">{e.statut}</Badge>;
  return null;
};

const Eleve = ({ e }) => (
  <span className="min-w-0">
    {e.eleve_id ? (
      <button type="button" onClick={() => ouvrirFiche(e.eleve_id)} title="Ouvrir la fiche"
        className="font-semibold text-left hover:underline" data-testid="essai-eleve">
        {e.eleve || "Élève sans nom"}
      </button>
    ) : <span className="font-semibold">{e.eleve || "—"}</span>}
    {e.age !== "" && e.age !== null && e.age !== undefined && (
      <span className="text-[var(--muted)]"> · {e.age} ans</span>
    )}
  </span>
);

const Contact = ({ e }) => (
  <div className="min-w-0 text-sm">
    {e.responsable && <div>{e.responsable}</div>}
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-0.5">
      {e.telephone && (
        <a href={telephone(e.telephone)} className="inline-flex items-center gap-1.5 font-medium whitespace-nowrap"
          style={{ color: LIEN }} data-testid="essai-tel">
          <Phone size={13} className="sans-impression" /> {e.telephone}
        </a>
      )}
      {e.email && (
        <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 min-w-0"
          style={{ color: LIEN, overflowWrap: "anywhere" }} data-testid="essai-email">
          <Mail size={13} className="shrink-0 sans-impression" /> <span className="min-w-0">{e.email}</span>
        </a>
      )}
      {!e.telephone && !e.email && <span className="text-[var(--muted)]">pas de contact</span>}
    </div>
  </div>
);

const heure = (e) => (e.debut ? `${e.debut}${e.fin ? `–${e.fin}` : ""}` : "—");
const profSalle = (e) => [e.professeur, e.salle].filter(Boolean).join(" · ") || "—";

export default function EssaisSemaine({ tour = 0, onNbSemaine }) {
  // "" : la semaine en cours, calculée par le serveur (heure suisse).
  const [periode, setPeriode] = useState({ du: "", au: "" });
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [choix, setChoix] = useState(null);
  const derniere = useRef(0);

  useEffect(() => {
    // Plusieurs clics rapides sur la flèche : seule la dernière réponse s'affiche.
    const numero = ++derniere.current;
    setErreur("");
    api(`/essais/semaine?${new URLSearchParams(periode)}`)
      .then((r) => {
        if (numero !== derniere.current) return;
        setD(r);
        if (onNbSemaine && r.du === r.cette_semaine?.du && r.au === r.cette_semaine?.au) onNbSemaine(r.nb);
      })
      .catch((e) => { if (numero === derniere.current) setErreur(e.message); });
  }, [periode, tour]);

  const pas = d ? ecart(d.du, d.au) + 1 : 7;
  const aller = (sens) => d && setPeriode({ du: decaler(d.du, sens * pas), au: decaler(d.au, sens * pas) });
  const cetteSemaine = !d || (d.du === d.cette_semaine?.du && d.au === d.cette_semaine?.au);
  const semaine = d && versDate(d.du).getDay() === 1 && ecart(d.du, d.au) === 6;

  const ouvrirChoix = () => setChoix(choix ? null : { du: d?.du || "", au: d?.au || "", erreur: "" });
  const appliquer = () => {
    if (!choix.du || !choix.au) { setChoix({ ...choix, erreur: "Choisis les deux dates." }); return; }
    if (choix.au < choix.du) { setChoix({ ...choix, erreur: "La date de fin est avant la date de début." }); return; }
    if (ecart(choix.du, choix.au) + 1 > JOURS_MAX) { setChoix({ ...choix, erreur: "Choisis une période de trois mois au plus." }); return; }
    setPeriode({ du: choix.du, au: choix.au });
    setChoix(null);
  };

  // Un groupe par jour, dans l'ordre du serveur (jour, puis heure du cours).
  const jours = [];
  (d?.items || []).forEach((e) => {
    const dernier = jours[jours.length - 1];
    if (dernier && dernier.date === e.date) dernier.items.push(e);
    else jours.push({ date: e.date, titre: e.jour_complet, items: [e] });
  });

  const vide = cetteSemaine ? "Aucun cours d'essai cette semaine."
    : semaine ? "Aucun cours d'essai cette semaine-là." : "Aucun cours d'essai sur ces dates.";

  return (
    <div className="space-y-5 fiche-impression" data-testid="essais-semaine">
      <div className="space-y-3">
        <h2 className="font-display text-2xl tracking-tight">{semaine || !d ? "Essais de la semaine" : "Essais"}</h2>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 w-full sm:w-auto">
            <button type="button" onClick={() => aller(-1)} disabled={!d} aria-label="Période précédente"
              className="btn-ghost shrink-0 !px-2.5 sans-impression" data-testid="essais-precedent">
              <ChevronLeft size={18} />
            </button>
            <div className="flex-1 sm:flex-none text-center font-semibold px-2 min-w-0 sm:min-w-[250px]" data-testid="essais-periode">
              {d ? libellePeriode(d.du, d.au) : "…"}
            </div>
            <button type="button" onClick={() => aller(1)} disabled={!d} aria-label="Période suivante"
              className="btn-ghost shrink-0 !px-2.5 sans-impression" data-testid="essais-suivant">
              <ChevronRight size={18} />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 sans-impression">
            {!cetteSemaine && (
              <button type="button" className="btn-ghost" onClick={() => { setChoix(null); setPeriode({ du: "", au: "" }); }}
                data-testid="essais-cette-semaine">
                Cette semaine
              </button>
            )}
            <button type="button" className="btn-ghost" onClick={ouvrirChoix} data-testid="essais-choisir"
              style={choix ? { borderColor: "var(--primary)", color: "var(--text)" } : undefined}>
              <CalendarDays size={15} /> Choisir les dates
            </button>
            {d && d.items.length > 0 && (
              <button type="button" className="btn-ghost" onClick={() => window.print()} data-testid="essais-imprimer">
                <Printer size={15} /> Imprimer
              </button>
            )}
          </div>
        </div>

        {choix && (
          <div className="sans-impression bg-white rounded-2xl border p-4 flex flex-wrap items-end gap-3"
            style={{ borderColor: "var(--border)" }} data-testid="essais-dates">
            <label className="text-xs text-[var(--muted)] flex-1 min-w-[140px] sm:flex-none">
              Du
              <input type="date" className="input-field mt-1 w-full" value={choix.du}
                onChange={(e) => setChoix({ ...choix, du: e.target.value, erreur: "" })} data-testid="essais-du" />
            </label>
            <label className="text-xs text-[var(--muted)] flex-1 min-w-[140px] sm:flex-none">
              Au
              <input type="date" className="input-field mt-1 w-full" value={choix.au} min={choix.du || undefined}
                onChange={(e) => setChoix({ ...choix, au: e.target.value, erreur: "" })} data-testid="essais-au" />
            </label>
            <button type="button" className="btn-primary" onClick={appliquer} data-testid="essais-voir">Voir</button>
            {choix.erreur && <p className="w-full text-sm" style={{ color: "#B91C1C" }}>{choix.erreur}</p>}
          </div>
        )}

        {d && d.nb + d.annules > 0 && (
          <p className="text-sm text-[var(--muted)]" data-testid="essais-total">
            {d.nb === 0 ? "" : `${d.nb} essai${d.nb > 1 ? "s" : ""}`}
            {d.annules > 0 && (
              <span className="text-xs" data-testid="essais-annules">
                {d.nb === 0 ? "" : " · "}{d.annules} annulé{d.annules > 1 ? "s" : ""}
              </span>
            )}
          </p>
        )}
      </div>

      {erreur && <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="essais-erreur">{erreur}</p>}
      {!d && !erreur && <p className="text-[var(--muted)]">Chargement…</p>}
      {d && jours.length === 0 && (
        <div className="bg-white rounded-2xl border px-5 py-10 text-center text-[var(--muted)]"
          style={{ borderColor: "var(--border)" }} data-testid="essais-vide">
          {vide}
        </div>
      )}

      {jours.map((j) => (
        <section key={j.date} className="space-y-2" data-testid="essais-jour">
          <h3 className="flex items-center gap-2 font-semibold text-base">
            {j.titre}
            <span className="text-xs font-normal text-[var(--muted)]">{j.items.length} essai{j.items.length > 1 ? "s" : ""}</span>
            {j.date === d.aujourdhui && <Badge ton="bleu">aujourd'hui</Badge>}
          </h3>

          {/* Grand écran et papier : une ligne par essai. */}
          <div className="hidden md:block print:block bg-white rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }}>
            {j.items.map((e, k) => (
              <div key={e.id || k} className="hidden md:grid print:grid gap-4 print:gap-3 px-5 print:px-3 py-3 print:py-2 items-start text-sm"
                style={{ gridTemplateColumns: COLONNES, borderTop: k ? "1px solid var(--border, #E2E8F0)" : "none" }}
                data-testid="essai-ligne">
                <div className="font-semibold tabular-nums whitespace-nowrap">{heure(e)}</div>
                <div className="font-semibold min-w-0">{e.cours || "—"}</div>
                <div className="min-w-0 space-y-1">
                  <Eleve e={e} />
                  <div><Etat e={e} /></div>
                </div>
                <div className="text-[var(--muted)] min-w-0">{profSalle(e)}</div>
                <Contact e={e} />
              </div>
            ))}
          </div>

          {/* Téléphone : une carte par essai. */}
          <div className="md:hidden print:hidden space-y-2">
            {j.items.map((e, k) => (
              <div key={e.id || k} className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }}
                data-testid="essai-carte">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold tabular-nums">{heure(e)}</div>
                    <div className="font-semibold">{e.cours || "—"}</div>
                    <div className="text-xs text-[var(--muted)]">{profSalle(e)}</div>
                  </div>
                  <Etat e={e} />
                </div>
                <div className="mt-3 pt-3 border-t space-y-1" style={{ borderColor: "var(--border, #E2E8F0)" }}>
                  <div><Eleve e={e} /></div>
                  <Contact e={e} />
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
